/**
 * Admin Dashboard Page
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Users, 
  UserPlus, 
  UserCheck, 
  UserX,
  LogOut,
  Dumbbell,
  Mail,
  Phone,
  Calendar,
  MoreVertical,
  Loader2,
  Check,
  X,
  Eye,
  Library,
  ClipboardList,
  MessageCircle,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useAdminStats, useClients } from "../hooks/useApi";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Toaster, toast } from "sonner";

const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 }
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { stats, isLoading: statsLoading, mutate: mutateStats } = useAdminStats();
  const { clients, isLoading: clientsLoading, mutate: mutateClients } = useClients();
  
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newClient, setNewClient] = useState({
    email: "",
    password: "",
    first_name: "",
    last_name: "",
    phone: "",
    plan_name: "Basic",
    has_nutrition: false,
    has_messaging: false,
    has_habits: false,
  });
  
  const handleCreateClient = async (e) => {
    e.preventDefault();
    setCreating(true);
    
    try {
      await api.post("/api/admin/clients", newClient);
      toast.success("Client créé avec succès");
      setCreateDialogOpen(false);
      setNewClient({
        email: "",
        password: "",
        first_name: "",
        last_name: "",
        phone: "",
        plan_name: "Basic",
        has_nutrition: false,
        has_messaging: false,
        has_habits: false,
      });
      mutateClients();
      mutateStats();
    } catch (err) {
      toast.error(err.message || "Erreur lors de la création");
    } finally {
      setCreating(false);
    }
  };
  
  const handleLogout = async () => {
    await logout();
    window.location.href = "/login";
  };
  
  return (
    <div className="min-h-screen bg-zinc-950">
      <Toaster position="top-right" richColors />
      <div className="noise-overlay" />
      
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-lime-400/10 rounded-xl flex items-center justify-center">
                <Dumbbell className="w-5 h-5 text-lime-400" />
              </div>
              <div>
                <h1 className="font-heading font-bold text-xl text-white tracking-tight">
                  MYGYMCOPILOT
                </h1>
                <p className="text-xs text-zinc-500">Espace Coach</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <span className="text-sm text-zinc-400 hidden sm:block">
                {user?.email}
              </span>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/messages")}
                data-testid="admin-messages-button"
                className="text-zinc-400 hover:text-indigo-400 hover:bg-zinc-800"
              >
                <MessageCircle className="w-5 h-5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleLogout}
                data-testid="logout-button"
                className="text-zinc-400 hover:text-white hover:bg-zinc-800"
              >
                <LogOut className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </header>
      
      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="show"
          className="space-y-8"
        >
          {/* Stats Cards */}
          <motion.section variants={itemVariants}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-2xl font-bold text-white tracking-tight">
                TABLEAU DE BORD
              </h2>
              
              <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
                <DialogTrigger asChild>
                  <Button 
                    data-testid="create-client-button"
                    className="h-11 px-5 bg-lime-400 hover:bg-lime-500 text-zinc-950 font-bold uppercase tracking-wider rounded-full"
                  >
                    <UserPlus className="w-4 h-4 mr-2" />
                    Nouveau Client
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
                  <DialogHeader>
                    <DialogTitle className="font-heading text-xl text-white">
                      Créer un compte client
                    </DialogTitle>
                    <DialogDescription className="text-zinc-400">
                      Remplissez les informations du nouveau client
                    </DialogDescription>
                  </DialogHeader>
                  
                  <form onSubmit={handleCreateClient} className="space-y-4 mt-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-zinc-300">Prénom</Label>
                        <Input
                          value={newClient.first_name}
                          onChange={(e) => setNewClient({...newClient, first_name: e.target.value})}
                          placeholder="Jean"
                          data-testid="create-client-firstname"
                          className="bg-zinc-950 border-zinc-800 h-11"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-zinc-300">Nom</Label>
                        <Input
                          value={newClient.last_name}
                          onChange={(e) => setNewClient({...newClient, last_name: e.target.value})}
                          placeholder="Dupont"
                          data-testid="create-client-lastname"
                          className="bg-zinc-950 border-zinc-800 h-11"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="text-zinc-300">Email *</Label>
                      <Input
                        type="email"
                        value={newClient.email}
                        onChange={(e) => setNewClient({...newClient, email: e.target.value})}
                        placeholder="client@email.com"
                        required
                        data-testid="create-client-email"
                        className="bg-zinc-950 border-zinc-800 h-11"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="text-zinc-300">Mot de passe *</Label>
                      <Input
                        type="password"
                        value={newClient.password}
                        onChange={(e) => setNewClient({...newClient, password: e.target.value})}
                        placeholder="••••••••"
                        required
                        minLength={8}
                        data-testid="create-client-password"
                        className="bg-zinc-950 border-zinc-800 h-11"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="text-zinc-300">Téléphone</Label>
                      <Input
                        value={newClient.phone}
                        onChange={(e) => setNewClient({...newClient, phone: e.target.value})}
                        placeholder="+33 6 12 34 56 78"
                        data-testid="create-client-phone"
                        className="bg-zinc-950 border-zinc-800 h-11"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="text-zinc-300">Formule</Label>
                      <Select
                        value={newClient.plan_name}
                        onValueChange={(value) => setNewClient({...newClient, plan_name: value})}
                      >
                        <SelectTrigger 
                          data-testid="create-client-plan"
                          className="bg-zinc-950 border-zinc-800 h-11"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-900 border-zinc-800">
                          <SelectItem value="Basic">Basic</SelectItem>
                          <SelectItem value="Premium">Premium</SelectItem>
                          <SelectItem value="VIP">VIP</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-4 pt-4 border-t border-zinc-800">
                      <Label className="text-zinc-300 text-sm font-bold uppercase tracking-wider">
                        Modules activés
                      </Label>
                      
                      <div className="flex items-center justify-between">
                        <span className="text-zinc-400">Nutrition</span>
                        <Switch
                          checked={newClient.has_nutrition}
                          onCheckedChange={(checked) => setNewClient({...newClient, has_nutrition: checked})}
                          data-testid="toggle-nutrition"
                        />
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <span className="text-zinc-400">Messagerie</span>
                        <Switch
                          checked={newClient.has_messaging}
                          onCheckedChange={(checked) => setNewClient({...newClient, has_messaging: checked})}
                          data-testid="toggle-messaging"
                        />
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <span className="text-zinc-400">Habitudes</span>
                        <Switch
                          checked={newClient.has_habits}
                          onCheckedChange={(checked) => setNewClient({...newClient, has_habits: checked})}
                          data-testid="toggle-habits"
                        />
                      </div>
                    </div>
                    
                    <Button
                      type="submit"
                      disabled={creating}
                      data-testid="submit-create-client"
                      className="w-full h-12 bg-lime-400 hover:bg-lime-500 text-zinc-950 font-bold uppercase tracking-wider rounded-full mt-4"
                    >
                      {creating ? <Loader2 className="w-5 h-5 animate-spin" /> : "Créer le compte"}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Card className="bg-zinc-900/50 border-zinc-800 hover:border-lime-400/30 transition-colors">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-lime-400/10 rounded-xl flex items-center justify-center">
                      <Users className="w-6 h-6 text-lime-400" />
                    </div>
                    <div>
                      <p className="text-zinc-500 text-sm font-medium uppercase tracking-wider">
                        Total Clients
                      </p>
                      <p className="text-3xl font-heading font-bold text-white">
                        {statsLoading ? "..." : stats?.total_clients || 0}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-zinc-900/50 border-zinc-800 hover:border-lime-400/30 transition-colors">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-emerald-400/10 rounded-xl flex items-center justify-center">
                      <UserCheck className="w-6 h-6 text-emerald-400" />
                    </div>
                    <div>
                      <p className="text-zinc-500 text-sm font-medium uppercase tracking-wider">
                        Actifs
                      </p>
                      <p className="text-3xl font-heading font-bold text-white">
                        {statsLoading ? "..." : stats?.active_clients || 0}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-zinc-900/50 border-zinc-800 hover:border-lime-400/30 transition-colors">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-red-400/10 rounded-xl flex items-center justify-center">
                      <UserX className="w-6 h-6 text-red-400" />
                    </div>
                    <div>
                      <p className="text-zinc-500 text-sm font-medium uppercase tracking-wider">
                        Inactifs
                      </p>
                      <p className="text-3xl font-heading font-bold text-white">
                        {statsLoading ? "..." : stats?.inactive_clients || 0}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </motion.section>
          
          {/* Quick Access - Epic 3 */}
          <motion.section variants={itemVariants}>
            <h2 className="font-heading text-2xl font-bold text-white tracking-tight mb-4">
              GESTION
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Card 
                className="bg-zinc-900/50 border-zinc-800 hover:border-sky-400/30 transition-colors cursor-pointer"
                onClick={() => navigate("/admin/exercises")}
                data-testid="exercises-link"
              >
                <CardContent className="p-5 flex items-center gap-4">
                  <div className="w-14 h-14 bg-sky-400/10 rounded-xl flex items-center justify-center">
                    <Library className="w-7 h-7 text-sky-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">Bibliothèque d'Exercices</h3>
                    <p className="text-zinc-500 text-sm">Gérer les exercices et vidéos</p>
                  </div>
                </CardContent>
              </Card>
              
              <Card 
                className="bg-zinc-900/50 border-zinc-800 hover:border-purple-400/30 transition-colors cursor-pointer"
                onClick={() => navigate("/admin/templates")}
                data-testid="templates-link"
              >
                <CardContent className="p-5 flex items-center gap-4">
                  <div className="w-14 h-14 bg-purple-400/10 rounded-xl flex items-center justify-center">
                    <ClipboardList className="w-7 h-7 text-purple-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">Programmes d'Entraînement</h3>
                    <p className="text-zinc-500 text-sm">Créer et gérer les séances</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </motion.section>
          
          {/* Clients List */}
          <motion.section variants={itemVariants}>
            <h2 className="font-heading text-2xl font-bold text-white tracking-tight mb-6">
              MES CLIENTS
            </h2>
            
            {clientsLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-lime-400" />
              </div>
            ) : clients.length === 0 ? (
              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardContent className="py-12 text-center">
                  <Users className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
                  <p className="text-zinc-500">Aucun client pour le moment</p>
                  <p className="text-zinc-600 text-sm mt-1">
                    Créez votre premier client avec le bouton ci-dessus
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {clients.map((client, index) => (
                  <motion.div
                    key={client.user.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Card 
                      className="bg-zinc-900/50 border-zinc-800 hover:border-zinc-700 transition-colors"
                      data-testid={`client-card-${client.user.id}`}
                    >
                      <CardContent className="p-4 sm:p-6">
                        <div className="flex items-center justify-between gap-4">
                          <div className="flex items-center gap-4 flex-1 min-w-0">
                            <div className="w-12 h-12 bg-zinc-800 rounded-full flex items-center justify-center flex-shrink-0">
                              <span className="text-lime-400 font-bold text-lg">
                                {(client.profile?.first_name?.[0] || client.user.email[0]).toUpperCase()}
                              </span>
                            </div>
                            
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <h3 className="font-semibold text-white truncate">
                                  {client.profile?.first_name && client.profile?.last_name
                                    ? `${client.profile.first_name} ${client.profile.last_name}`
                                    : client.user.email}
                                </h3>
                                <Badge 
                                  variant={client.user.is_active ? "default" : "secondary"}
                                  className={client.user.is_active 
                                    ? "bg-emerald-400/10 text-emerald-400 border-emerald-400/30" 
                                    : "bg-zinc-800 text-zinc-500"}
                                >
                                  {client.user.is_active ? "Actif" : "Inactif"}
                                </Badge>
                              </div>
                              
                              <div className="flex items-center gap-4 mt-1 text-sm text-zinc-500">
                                <span className="flex items-center gap-1 truncate">
                                  <Mail className="w-3 h-3" />
                                  {client.user.email}
                                </span>
                                {client.profile?.phone && (
                                  <span className="flex items-center gap-1 hidden sm:flex">
                                    <Phone className="w-3 h-3" />
                                    {client.profile.phone}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-4">
                            {/* Feature badges */}
                            <div className="hidden md:flex items-center gap-2">
                              {client.features?.has_nutrition && (
                                <Badge className="bg-sky-400/10 text-sky-400 border-sky-400/30">
                                  Nutrition
                                </Badge>
                              )}
                              {client.features?.has_messaging && (
                                <Badge className="bg-purple-400/10 text-purple-400 border-purple-400/30">
                                  Messages
                                </Badge>
                              )}
                              {client.features?.has_habits && (
                                <Badge className="bg-amber-400/10 text-amber-400 border-amber-400/30">
                                  Habitudes
                                </Badge>
                              )}
                            </div>
                            
                            <div className="flex items-center gap-2">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => navigate(`/admin/clients/${client.profile?.id}`)}
                                data-testid={`view-client-${client.user.id}`}
                                className="text-zinc-400 hover:text-lime-400"
                              >
                                <Eye className="w-5 h-5" />
                              </Button>
                              
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button 
                                    variant="ghost" 
                                    size="icon"
                                    className="text-zinc-400 hover:text-white"
                                  >
                                    <MoreVertical className="w-5 h-5" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent 
                                  align="end"
                                  className="bg-zinc-900 border-zinc-800"
                                >
                                  <DropdownMenuItem 
                                    className="text-zinc-300 focus:text-white focus:bg-zinc-800"
                                    onClick={() => navigate(`/admin/clients/${client.profile?.id}`)}
                                  >
                                    <Eye className="w-4 h-4 mr-2" />
                                    Voir le profil
                                  </DropdownMenuItem>
                                  <DropdownMenuItem className="text-zinc-300 focus:text-white focus:bg-zinc-800">
                                    Assigner un programme
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            )}
          </motion.section>
        </motion.div>
      </main>
    </div>
  );
}
